import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { api } from '../../lib/api';
import { downloadAuthFile } from '../../lib/authImage';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { DownloadIcon } from '../../components/icons';
import { errorMessage, formatDateTime } from '../members/hooks';
import { actionLabel, actorLabel, FAMILY_DOT, FAMILY_LABEL, originLabel, type AuditFamily, type QrAuditEntry, type QrAuditPage } from './types';

const FAMILIES: AuditFamily[] = ['owner', 'destination', 'version', 'lifecycle', 'scan', 'operator', 'config'];

function describeValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value !== 'object') return String(value);
  const entries = Object.entries(value as Record<string, unknown>).filter(([, v]) => v !== null && v !== undefined);
  if (entries.length === 0) return '—';
  return entries.map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`).join(' · ');
}

function hasChange(entry: QrAuditEntry): boolean {
  return (entry.before !== null && entry.before !== undefined) || (entry.after !== null && entry.after !== undefined);
}

export interface QrTimelineProps {
  code: string;
  /** Organización activa cuando la mira el partner; el operador no la manda. */
  orgId?: string;
  /** Eventos por página; en la ficha se usan pocos y sin filtros. */
  limit?: number;
  /** Vista corta para la ficha: sin filtros, sin exportar y sin «cargar más». */
  compact?: boolean;
  /** Acceso a la línea de tiempo completa cuando se muestra corta. */
  footer?: ReactNode;
  /**
   * De quién es la consola que la muestra. El partner lee `/v1/audit/qr/{code}` con su organización; el
   * operador trabaja sobre todas y lee `/v1/operator/qrs/{code}/audit`, que no pide `X-Org-Id` (BUG-25).
   */
  source?: 'partner' | 'operator';
}

/** Línea de tiempo de un código (R6): riel vertical, antes→después, motivo, filtros y exportación. */
export function QrTimeline({ code, orgId, limit = 50, compact = false, footer, source = 'partner' }: QrTimelineProps) {
  const [family, setFamily] = useState<AuditFamily | ''>('');
  const [items, setItems] = useState<QrAuditEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // Se separan: el fallo de la carga reemplaza la lista, el de la exportación solo avisa.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const base = source === 'operator' ? `/v1/operator/qrs/${encodeURIComponent(code)}/audit` : `/v1/audit/qr/${encodeURIComponent(code)}`;

  const path = useCallback(
    (cursor: string | null) => {
      const params = new URLSearchParams();
      if (family) params.set('family', family);
      params.set('limit', String(limit));
      if (cursor) params.set('cursor', cursor);
      return `${base}?${params.toString()}`;
    },
    [base, family, limit],
  );

  const load = useCallback(
    async (cursor: string | null) => {
      setLoadError(null);
      if (cursor) setLoadingMore(true);
      else setLoading(true);
      try {
        const page = await api.get<QrAuditPage>(path(cursor), { orgId });
        setItems((prev) => (cursor ? [...prev, ...(page.items ?? [])] : (page.items ?? [])));
        setNextCursor(page.nextCursor ?? null);
      } catch (err) {
        if (!cursor) setItems([]);
        setLoadError(errorMessage(err));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [path, orgId],
  );

  useEffect(() => {
    void load(null);
  }, [load]);

  async function exportCsv() {
    setExportError(null);
    try {
      await downloadAuthFile(`${base}/export.csv`, `${code}-auditoria.csv`, { orgId });
    } catch (err) {
      setExportError(errorMessage(err));
    }
  }

  return (
    <div className="space-y-3">
      {!compact && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line bg-white p-3">
          <div className="flex flex-wrap gap-1" role="group" aria-label="Filtrar eventos">
            {(['', ...FAMILIES] as (AuditFamily | '')[]).map((f) => (
              <button
                key={f || 'all'}
                type="button"
                aria-pressed={family === f}
                onClick={() => setFamily(f)}
                className={`rounded-full px-3 py-1.5 text-sm ${family === f ? 'bg-yellow-soft font-semibold text-ink' : 'border border-line text-ink-muted hover:bg-gray-50'}`}
              >
                {f ? FAMILY_LABEL[f] : 'Todo'}
              </button>
            ))}
          </div>
          <Button variant="secondary" size="sm" onClick={() => void exportCsv()}>
            <DownloadIcon />
            Exportar CSV
          </Button>
        </div>
      )}

      {exportError && <Banner tone="error">{exportError}</Banner>}

      <div className="rounded-lg border border-line bg-white p-4">
        {loading ? (
          <div role="status" className="text-sm text-ink-muted">
            Cargando la línea de tiempo…
          </div>
        ) : loadError ? (
          <Banner
            tone="error"
            action={
              <Button size="sm" variant="secondary" onClick={() => void load(null)}>
                Reintentar
              </Button>
            }
          >
            No pudimos cargar la línea de tiempo. {loadError}
          </Banner>
        ) : items.length === 0 ? (
          <p className="text-sm text-ink-muted">
            {family ? `Sin eventos de ${FAMILY_LABEL[family].toLowerCase()} para este código.` : 'Este código todavía no tiene eventos registrados.'}
          </p>
        ) : (
          <ol className="space-y-4">
            {items.map((entry) => {
              const fam = (entry.family ?? '') as AuditFamily;
              const dot = FAMILY_DOT[fam] ?? '#E4E1D8';
              return (
                <li key={entry.id} className="relative grid grid-cols-[auto,1fr] gap-3 pb-1">
                  <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: dot }} aria-hidden="true" />
                  <div className="space-y-0.5">
                    <time className="block text-xs tabular-nums text-ink-muted">{formatDateTime(entry.at)}</time>
                    <p className="font-semibold text-ink">{entry.summary ?? actionLabel(entry.action)}</p>
                    <p className="text-xs text-ink-muted">
                      {typeof entry.actor === 'string' ? entry.actor : actorLabel(entry.actor ?? null)} · {originLabel(entry.origin)}
                    </p>
                    {hasChange(entry) && (
                      <p className="rounded-md bg-mist px-2 py-1 text-xs">
                        <span className="text-ink-muted line-through">{describeValue(entry.before)}</span>
                        <span className="px-1 text-ink-muted" aria-hidden="true">
                          →
                        </span>
                        <span className="break-all text-ink">{describeValue(entry.after)}</span>
                      </p>
                    )}
                    {entry.reason && <p className="text-xs text-ink-muted">Motivo: «{entry.reason}»</p>}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
        {footer && <div className="mt-3">{footer}</div>}
      </div>

      {!compact && nextCursor && !loading && !loadError && (
        <div className="flex justify-center">
          <Button variant="secondary" loading={loadingMore} onClick={() => void load(nextCursor)}>
            Cargar más
          </Button>
        </div>
      )}
      {!compact && (
        <p className="text-xs text-ink-muted">
          Registro de solo lectura: no se puede editar ni borrar, y sobrevive a la eliminación del código.
        </p>
      )}
    </div>
  );
}
